import { useCallback, useContext, useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import { TOContext } from "context/TContext";
import { formatNumber, priceTransform } from "helpers/formatNumber";
import { getSuggestedTipAmount } from "helpers/getSuggestedTipAmount";

export const useDriverTip = () => {
  const theme = useTheme();
  const [hasChange, setHasChange] = useState(false);
  const [hasUserInteraction, setHasUserInteraction] = useState(false);
  const [tipValue, setTipValue] = useState("0.00");
  const [lastAppliedTip, setLastAppliedTip] = useState(null);
  const {
    selectedDriverTip,
    setSelectedDriverTip,
    charges,
    setCharges,
    aggregatedTipOptions,
  } = useContext(TOContext);

  const updateChargesSafely = useCallback(
    (tipAmount) => {
      setCharges((prevCharges) => ({
        ...prevCharges,
        tipInfo: {
          ...prevCharges?.tipInfo,
          driver: Number(tipAmount) || 0,
        },
        balanceDue:
          prevCharges?.balanceDue -
          (prevCharges?.tipInfo?.driver || 0) +
          (Number(tipAmount) || 0),
        total:
          prevCharges?.total -
          (prevCharges?.tipInfo?.driver || 0) +
          (Number(tipAmount) || 0),
      }));
    },
    [setCharges],
  );

  const handleSelectTip = (item) => {
    const numericAmount = getSuggestedTipAmount(
      charges?.subTotal,
      item,
      aggregatedTipOptions?.driverTip,
    );
    setSelectedDriverTip(item);
    const formattedValue = formatNumber(numericAmount);
    setTipValue(formattedValue || "0.00");
    setHasUserInteraction(true);
    // Auto-apply to charges so it's visible immediately in Charges component
    updateChargesSafely(numericAmount);
  };

  useEffect(() => {
    // Don't override an explicit tip selection the user has made
    if (hasUserInteraction) return;

    if (
      !charges?.tipInfo?.driver ||
      charges?.tipInfo?.driver === 0 ||
      charges?.tipInfo?.driver === 0.0
    ) {
      if (aggregatedTipOptions?.driverTip && charges?.subTotal) {
        const defaultTip =
          aggregatedTipOptions.driverTip[
            aggregatedTipOptions.defaultSelectedDriverIndex
          ];
        // Only auto-apply the default preset on the very first load (before
        // the user has confirmed any tip). Once lastAppliedTip is non-null the
        // user has confirmed a value (even 0) and we must not override it.
        if (defaultTip && lastAppliedTip === null) {
          const numericAmountParse = getSuggestedTipAmount(
            charges?.subTotal,
            defaultTip,
            aggregatedTipOptions?.driverTip,
          );
          const convertedTip = formatNumber(numericAmountParse);

          setTipValue(convertedTip || "0.00");
          setSelectedDriverTip(defaultTip);
          if (
            numericAmountParse > 0 &&
            charges?.tipInfo?.driver !== numericAmountParse
          ) {
            updateChargesSafely(numericAmountParse);
            setLastAppliedTip(numericAmountParse);
          }
        }
      }
      return;
    }
    if (
      charges &&
      charges?.tipInfo &&
      charges?.tipInfo?.driver &&
      aggregatedTipOptions?.driverTip
    ) {
      const chargesTipRounded =
        Number(charges?.tipInfo?.driver?.toFixed(2)) || 0;
      const matchingTipOption = aggregatedTipOptions.driverTip.find((tip) => {
        const suggested = getSuggestedTipAmount(
          charges?.subTotal,
          tip,
          aggregatedTipOptions?.driverTip,
        );
        return Math.abs(suggested - chargesTipRounded) < 0.01;
      });

      if (matchingTipOption) {
        setSelectedDriverTip(matchingTipOption);
        const numericAmount = getSuggestedTipAmount(
          charges?.subTotal,
          matchingTipOption,
          aggregatedTipOptions?.driverTip,
        );
        setTipValue(formatNumber(numericAmount) || "0.00");
      } else {
        setTipValue(formatNumber(charges?.tipInfo?.driver) || "0.00");
      }
    } else {
      if (aggregatedTipOptions?.driverTip) {
        const defaultTip =
          aggregatedTipOptions.driverTip[
            aggregatedTipOptions.defaultSelectedDriverIndex
          ];
        const numericAmountParse = getSuggestedTipAmount(
          charges?.subTotal,
          defaultTip,
          aggregatedTipOptions?.driverTip,
        );
        const convertedTip = formatNumber(numericAmountParse);

        setTipValue(convertedTip || "0.00");
        setSelectedDriverTip(defaultTip);
        // Don't update charges automatically - user must click ADD to apply
      }
    }
  }, [
    hasUserInteraction,
    lastAppliedTip,
    charges,
    charges?.tipInfo?.driver,
    charges?.subTotal,
    aggregatedTipOptions,
    updateChargesSafely,
    setSelectedDriverTip,
  ]);

  const tipStyleMemo = useCallback(
    (tipId) => {
      const activeTip = `2px solid ${theme.palette.warning.main}`;
      const inActiveTip = `1px solid ${theme.palette.grey[900]}`;
      return selectedDriverTip?.id === tipId ? activeTip : inActiveTip;
    },
    [selectedDriverTip?.id, theme.palette.grey, theme.palette.warning.main],
  );

  const handleChangeTip = (event) => {
    const value = priceTransform(event.target.value);
    if (value !== null) {
      setTipValue(formatNumber(value) || "0.00");
      setHasUserInteraction(true);
    }
  };

  // Track the initial/last applied tip value from charges.
  // Only initialise from the backend when the server already has a non-zero
  // driver tip, so that a 0-tip order keeps lastAppliedTip === null and lets
  // the main effect perform its one-time default-preset auto-apply.
  useEffect(() => {
    if (charges?.tipInfo?.driver !== undefined) {
      const initialTip = Number(charges?.tipInfo?.driver?.toFixed(2)) || 0;
      if (lastAppliedTip === null && initialTip > 0) {
        setLastAppliedTip(initialTip);
      }
    }
  }, [charges?.tipInfo?.driver, lastAppliedTip]);

  // Update hasChange when tipValue differs from last applied tip
  useEffect(() => {
    const numericTipValue = parseFloat(tipValue.replace(/,/g, "")) || 0;
    const numericLastAppliedTip = lastAppliedTip !== null ? lastAppliedTip : 0;
    const hasDifference =
      Math.abs(numericTipValue - numericLastAppliedTip) > 0.01;
    setHasChange(hasDifference);
  }, [tipValue, lastAppliedTip]);

  const handleClick = () => {
    const numericAmount = parseFloat(tipValue.replace(/,/g, "")) || 0;
    updateChargesSafely(numericAmount);
    setLastAppliedTip(numericAmount);
    setSelectedDriverTip(null);
    setHasUserInteraction(false);
    // hasChange will be automatically updated by the comparison useEffect
  };

  return {
    tipStyleMemo,
    handleSelectTip,
    tipValue,
    selectedDriverTip,
    loadingApplyTip: false,
    aggregatedTipOptions,
    handleChangeTip,
    handleClick,
    hasChange,
    hasUserInteraction,
    manualLoadingTip: false,
  };
};
